/**
 * DashboardSection - Admin dashboard overview
 * Shows key stats: Users, Finance, Deposits, System
 *
 * Uses shared components:
 * - StatsCard (core) - stat display cards
 * - MenuButton (composed) - navigation menu
 */

import {
  Users,
  DollarSign,
  CreditCard,
  Clock,
  TrendingUp,
  TrendingDown,
  Zap,
  LayoutDashboard,
  ArrowDownToLine,
  ArrowUpFromLine,
  FileText,
  Gift,
  UserPlus,
  Database,
  Settings,
} from 'lucide-react';
import { StatsCard, MenuButton } from '@/components/shared';
import type { MenuOption } from '@/components/shared/composed/MenuButton';
import type { DashboardResponse } from '@/types';

export interface DashboardSectionProps {
  /** Dashboard stats data - matches DashboardResponse directly */
  stats: DashboardResponse | null;
  /** Loading state */
  isLoading?: boolean;
  /** Callback to refresh data */
  onRefresh?: () => void;
  /** Callback when navigating to another page */
  onNavigate?: (page: string) => void;
}

// Admin menu options
const adminMenuOptions: MenuOption[] = [
  { value: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { value: 'users', label: 'Users', icon: Users },
  { value: 'deposits', label: 'Deposits', icon: ArrowDownToLine },
  { value: 'withdrawals', label: 'Withdrawals', icon: ArrowUpFromLine },
  { value: 'reports', label: 'Reports', icon: FileText },
  { value: 'bonus', label: 'Bonus', icon: Gift },
  { value: 'payments', label: 'Payments', icon: Settings },
  { value: 'referees', label: 'Referees', icon: Users },
];

/**
 * Format currency for display
 */
function formatCurrency(amount: number): string {
  return `${amount.toLocaleString()} ₼`;
}

export function DashboardSection({
  stats,
  isLoading = false,
  onNavigate,
}: DashboardSectionProps) {
  return (
    <div className="space-y-6 pb-6">
      {/* Header with menu */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Dashboard</h2>
        <MenuButton
          options={adminMenuOptions}
          value="dashboard"
          onSelect={(page) => onNavigate?.(page)}
          sheetTitle="Navigate"
        />
      </div>

      {/* Users Section */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Users</h3>
        <div className="space-y-4">
          <StatsCard
            title="Total Users"
            value={stats?.users.total ?? 0}
            subtitle={`${stats?.users.active ?? 0} active · ${stats?.users.banned ?? 0} banned`}
            icon={Users}
            loading={isLoading}
          />
          <StatsCard
            title="New Today"
            value={stats?.users.registeredToday ?? 0}
            subtitle="Registered today"
            icon={UserPlus}
            variant="info"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Finance Section */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Finance</h3>
        <div className="space-y-4">
          <StatsCard
            title="Net Profit"
            value={formatCurrency(stats?.finance.profit ?? 0)}
            icon={DollarSign}
            variant={stats?.finance.profit && stats.finance.profit > 0 ? 'success' : 'default'}
            loading={isLoading}
          />
          <StatsCard
            title="Total Deposits"
            value={formatCurrency(stats?.finance.totalDeposits ?? 0)}
            subtitle={`Today: ${formatCurrency(stats?.finance.todayDeposits ?? 0)}`}
            icon={TrendingUp}
            variant="success"
            loading={isLoading}
          />
          <StatsCard
            title="Total Withdrawals"
            value={formatCurrency(stats?.finance.totalWithdrawals ?? 0)}
            subtitle={`Today: ${formatCurrency(stats?.finance.todayWithdrawals ?? 0)}`}
            icon={TrendingDown}
            variant="error"
            loading={isLoading}
          />
        </div>
      </section>

      {/* Deposits Section */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Deposits</h3>
        <div className="space-y-4">
          <StatsCard
            title="Pending"
            value={stats?.deposits.pending ?? 0}
            subtitle="Awaiting payment"
            icon={Clock}
            variant="warning"
            loading={isLoading}
          />
          <StatsCard
            title="Completed Today"
            value={stats?.deposits.completedToday ?? 0}
            icon={CreditCard}
            variant="success"
            loading={isLoading}
          />
        </div>
      </section>

      {/* System Section */}
      <section>
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">System</h3>
        <div className="space-y-4">
          <StatsCard
            title="Database"
            value={stats?.system.dbConnected ? 'Connected' : 'Disconnected'}
            icon={Database}
            variant={stats?.system.dbConnected ? 'success' : 'error'}
            loading={isLoading}
          />
          <StatsCard
            title="Redis"
            value={stats?.system.redisConnected ? 'Connected' : 'Disconnected'}
            icon={Zap}
            variant={stats?.system.redisConnected ? 'success' : 'error'}
            loading={isLoading}
          />
        </div>
      </section>
    </div>
  );
}
